import axios from "axios";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  PRICING as DEFAULT_PRICING,
  SIZE_CLASSES as DEFAULT_SIZE_CLASSES,
  QUANTITY_TIERS as DEFAULT_QUANTITY_TIERS,
  URGENCY as DEFAULT_URGENCY,
  computePrice as computePriceDefault,
  formatCHF,
} from "@/lib/pricing";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PricingContext = createContext(null);

/**
 * Merge a partial override object over defaults. Only keys present in
 * the override replace default keys; unknown extras are ignored on read.
 *
 * For PRICING, SIZE_CLASSES, URGENCY: shallow-merge nested objects so
 * a partial override for a single verfahren still keeps other verfahren
 * intact.
 */
function mergeDicts(defaults, override) {
  if (!override || typeof override !== "object") return defaults;
  const out = { ...defaults };
  for (const [k, v] of Object.entries(override)) {
    if (v && typeof v === "object" && !Array.isArray(v)) {
      out[k] = { ...defaults[k], ...v };
    } else {
      out[k] = v;
    }
  }
  return out;
}

function mergeTiers(defaults, override) {
  if (!Array.isArray(override) || override.length === 0) return defaults;
  return override
    .filter((t) => typeof t?.min === "number" && typeof t?.pct === "number")
    .sort((a, b) => a.min - b.min);
}

/**
 * Pure computePrice that reads from an explicit config — mirrors
 * pricing.js#computePrice but sourced from context.
 */
function computePriceWith(cfg, { verfahren, size, quantity, urgency }) {
  const p = cfg.PRICING[verfahren];
  const s = cfg.SIZE_CLASSES[size];
  const u = cfg.URGENCY[urgency];
  const q = Number(quantity) || 0;
  if (!p || !s || !u || q < 1) return null;

  const surfacePrice = (p.base_chf_per_dm2 ?? 0) * (s.dm2 ?? 0);
  const setupPerPiece = (p.setup_chf ?? 0) / q;
  const rawUnit = Math.max(p.min_stueck_chf ?? 0, surfacePrice + setupPerPiece);

  let tier = null;
  let discountPct = 0;
  for (const t of cfg.QUANTITY_TIERS) {
    if (q >= t.min) { tier = t; discountPct = t.pct; }
  }
  const urgencyPct = u.pct ?? 0;
  const unit = rawUnit * (1 - discountPct / 100) * (1 + urgencyPct / 100);
  const total = unit * q;

  return {
    unitPrice: unit,
    totalPrice: total,
    totalPriceMin: total * 0.88,
    totalPriceMax: total * 1.12,
    discount: discountPct,
    urgencyPct,
    tier,
  };
}

export const PricingProvider = ({ children }) => {
  const [overrides, setOverrides] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/pricing/config`, { timeout: 8000 });
      setOverrides(data || {});
    } catch (_) {
      setOverrides({});
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(() => {
    const o = overrides || {};
    const PRICING       = mergeDicts(DEFAULT_PRICING,        o.pricing);
    const SIZE_CLASSES  = mergeDicts(DEFAULT_SIZE_CLASSES,   o.size_classes);
    const URGENCY       = mergeDicts(DEFAULT_URGENCY,        o.urgency);
    const QUANTITY_TIERS= mergeTiers(DEFAULT_QUANTITY_TIERS, o.quantity_tiers);
    const cfg = { PRICING, SIZE_CLASSES, URGENCY, QUANTITY_TIERS };
    return {
      ...cfg,
      loaded,
      refresh,
      overrides: o,
      computePrice: (args) => computePriceWith(cfg, args),
      formatCHF,
    };
  }, [overrides, loaded, refresh]);

  return (
    <PricingContext.Provider value={value}>
      {children}
    </PricingContext.Provider>
  );
};

export function usePricing() {
  const v = useContext(PricingContext);
  if (!v) {
    // Fallback: return defaults if provider missing (defensive during dev)
    return {
      PRICING: DEFAULT_PRICING,
      SIZE_CLASSES: DEFAULT_SIZE_CLASSES,
      QUANTITY_TIERS: DEFAULT_QUANTITY_TIERS,
      URGENCY: DEFAULT_URGENCY,
      computePrice: computePriceDefault,
      formatCHF,
      loaded: true,
      overrides: {},
      refresh: async () => {},
    };
  }
  return v;
}
